import { memo } from "react";
import type { FurnitureItem } from "./types";
import { getCatalogEntry, SEAT_WIDTH_CM, SEAT_PADDING_CM } from "./catalog";
import { renderShape } from "./shapes";
import { formatSince } from "../FloorplanRoomDetail";
import { OccupancyStripes, freeColor, hasOwnColor, occupiedFill, stripedPatternId } from "../occupancyFill";

interface FurnitureRendererProps {
  item: FurnitureItem;
  selected?: boolean;
  onClick?: (item: FurnitureItem) => void;
  onMouseDown?: (item: FurnitureItem, e: React.MouseEvent) => void;
  onRotateStart?: (item: FurnitureItem, e: React.MouseEvent) => void;
  /** Optional duplicate handle — a small icon next to the rotate handle. */
  onDuplicate?: (item: FurnitureItem) => void;
  scale?: number; // SVG units per meter (e.g. 35.8) — to convert cm sizes to SVG coordinates
  /** Tenant id of the parent room, used to colour desks by assignment match/mismatch. */
  roomTenantId?: number | null;
  /** Resolved fill colour of the parent room — desks with status="match" inherit it. */
  roomColor?: string;
  /** The Occupancy layer: off removes the in-use / free stripes (default on). */
  showOccupancy?: boolean;
  /** How desks are filled: "assignment" (default) colours by tenant match;
   *  "type" fills by operator desk type, and untyped desks get a dashed red outline. */
  deskColorMode?: "assignment" | "type";
}

function resolveDeskStatus(
  item: FurnitureItem,
  roomTenantId: number | null | undefined,
): "match" | "mismatch" | "user" | "unassigned" {
  if (item.effectiveUserId != null && item.assignmentSource === "explicit" && item.effectiveTenantId == null) {
    return "user";
  }
  if (item.effectiveTenantId != null) {
    return roomTenantId != null && item.effectiveTenantId === roomTenantId ? "match" : "mismatch";
  }
  return "unassigned";
}

export const FurnitureRenderer = memo(function FurnitureRenderer({ item, selected, onClick, onMouseDown, onRotateStart, onDuplicate, scale = 1, roomTenantId, roomColor, showOccupancy = true, deskColorMode = "assignment" }: FurnitureRendererProps) {
  const entry = getCatalogEntry(item.type);
  if (!entry) return null;
  // Every desk kind (single, double, L) takes the desk-type colour.
  const isDesk = entry.category === "desks";

  // Convert cm to SVG units: cm * (svgUnitsPerMeter / 100)
  const cmToSvg = scale / 100;
  let widthCm = item.params?.widthCm ?? item.params?.length ?? entry.width;
  let depthCm = item.params?.depthCm ?? entry.height;
  // Round tables: use explicit widthCm if set (from slider), otherwise derive from chair count
  if (item.type === "table" && item.params?.shape === "round") {
    if (!item.params?.widthCm && item.params?.size) {
      const diameter = Math.max(80, Math.round((item.params.size * 55) / Math.PI));
      widthCm = diameter;
    }
    depthCm = widthCm;
  }
  // For desks, depthCm is the desk surface depth; add chair space when chairs included
  if ((item.type === "desk-single" || item.type === "desk-l") && item.params?.includeChairs !== false && item.params?.depthCm) {
    depthCm = Math.round(depthCm / 0.55); // desk is 55% of total, chair is 45%
  }
  // desk-double: two desks facing each other + chairs on both sides
  if (item.type === "desk-double" && item.params?.includeChairs !== false && item.params?.depthCm) {
    depthCm = Math.round(depthCm * 2.8); // two desks + gap + two chair rows
  }
  // Grouped desks: multiply width by count
  if ((item.type === "desk-single" || item.type === "desk-double") && item.params?.count && item.params.count > 1) {
    widthCm = widthCm * item.params.count;
  }
  // L-sofa: compute size from seats per arm (~60cm per seat + 20cm padding + arm depth)
  if (item.type === "sofa-l") {
    const seatsA = item.params?.seatsA ?? 3;
    const seatsB = item.params?.seatsB ?? 3;
    const armDepth = item.params?.depthCm ?? 60; // fixed arm depth from params
    widthCm = seatsA * SEAT_WIDTH_CM + SEAT_PADDING_CM + armDepth;
    depthCm = seatsB * SEAT_WIDTH_CM + SEAT_PADDING_CM + armDepth;
  }
  const w = widthCm * cmToSvg;
  const h = depthCm * cmToSvg;

  return (
    <g
      transform={`translate(${item.x}, ${item.y}) rotate(${item.rotation})`}
      style={{ cursor: onClick ? "pointer" : "default" }}
      onClick={onClick ? (e) => { e.stopPropagation(); onClick(item); } : undefined}
      onMouseDown={onMouseDown ? (e) => { e.stopPropagation(); onMouseDown(item, e); } : undefined}
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* Center the shape */}
      <g transform={`translate(${-w / 2}, ${-h / 2})`}>
        {renderShape(
          item.type,
          w,
          h,
          item.params,
          item.rotation,
          cmToSvg,
          isDesk && deskColorMode === "type"
            ? { status: item.deskTypeColor ? "match" : "unassigned", showUserIcon: false, roomColor: item.deskTypeColor ?? undefined }
            : item.type === "desk-single" || item.type === "desk-l"
              ? {
                  status: resolveDeskStatus(item, roomTenantId),
                  showUserIcon: item.assignmentSource === "explicit" && item.effectiveUserId != null && item.effectiveTenantId == null,
                  roomColor,
                }
              : undefined,
        )}
        {/* In-use tint: red diagonal stripes while a presence sensor says it is occupied; a solid
         *  fill (green, or the room's own colour) while free; nothing without a reading.
         *  One mechanism for every item (a phone booth now, desks next). */}
        {showOccupancy && item.occupied != null && (() => {
          const withOwnColor = hasOwnColor(roomColor, item.occupied);
          const patternId = stripedPatternId(`item-${item.id}`);
          const fill = item.occupied ? occupiedFill(patternId) : freeColor(roomColor);
          return (
            <>
              {item.occupied && <defs><OccupancyStripes id={patternId} base={roomColor} stripe={5} /></defs>}
              <rect
                data-testid="furniture-occupancy-tint"
                data-occupied={item.occupied ? "true" : "false"}
                data-own-color={withOwnColor ? "true" : "false"}
                x={0}
                y={0}
                width={w}
                height={h}
                rx={3}
                fill={fill}
                fillOpacity={0.6}
                pointerEvents="none"
              >
                <title>{item.occupied ? `In use${item.occupiedSince ? ` since ${formatSince(item.occupiedSince)}` : ""}` : "Free"}</title>
              </rect>
            </>
          );
        })()}
      </g>

      {/* Broken overlay */}
      {item.brokenAmenities && item.brokenAmenities.length > 0 && (
        <>
          <rect
            x={-w / 2} y={-h / 2}
            width={w} height={h}
            fill="rgba(239,68,68,0.15)" stroke="none" rx={2}
          />
          <text
            x={w / 2 - 2} y={-h / 2 + Math.min(h * 0.3, 12)}
            textAnchor="end"
            fontSize={Math.min(w, h) * 0.25}
            fill="#ef4444"
            style={{ pointerEvents: "none" }}
          >
            ✕
          </text>
        </>
      )}

      {/* Untyped desk marker (desk-type colour mode) */}
      {deskColorMode === "type" && isDesk && !item.deskTypeId && (
        <rect
          x={-w / 2} y={-h / 2}
          width={w} height={h}
          fill="none" stroke="#ef4444" strokeDasharray="4 3" strokeWidth={1.5} rx={2}
          style={{ pointerEvents: "none" }}
        />
      )}

      {/* Selection border */}
      {selected && (
        <>
          <rect
            x={-w / 2 - 3} y={-h / 2 - 3}
            width={w + 6} height={h + 6}
            fill="none" stroke="#3b82f6" strokeWidth={1.5}
            strokeDasharray="4,3" rx={3}
          />
          {/* Rotation handle */}
          <line x1={0} y1={-h / 2 - 3} x2={0} y2={-h / 2 - 16}
            stroke="#3b82f6" strokeWidth={1} />
          <circle cx={0} cy={-h / 2 - 16} r={4}
            fill="#3b82f6" stroke="#fff" strokeWidth={1.2}
            style={{ cursor: "grab" }}
            data-rotation-handle="true"
            onMouseDown={onRotateStart ? (e) => { e.stopPropagation(); onRotateStart(item, e); } : undefined}
          />
          {/* Duplicate handle — small "copy" glyph offset right of the rotate
              handle so it doesn't fight for the same click target. */}
          {onDuplicate && (
            <g
              transform={`translate(${10}, ${-h / 2 - 16})`}
              style={{ cursor: "pointer" }}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); onDuplicate(item); }}
            >
              <title>Duplicate</title>
              <circle cx={0} cy={0} r={5} fill="#3b82f6" stroke="#fff" strokeWidth={1.2} />
              {/* Two overlapping squares = copy */}
              <rect x={-2.4} y={-2.4} width={3.5} height={3.5} fill="none" stroke="#fff" strokeWidth={0.9} rx={0.5} />
              <rect x={-1} y={-1} width={3.5} height={3.5} fill="#3b82f6" stroke="#fff" strokeWidth={0.9} rx={0.5} />
            </g>
          )}
        </>
      )}
    </g>
  );
});
